/**
 * React bindings for the arcade SDK: an auth/cloud context + hooks games use
 * to render sign-in, cloud saves, leaderboards, and audio controls.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ArcadeClient, type ArcadeUser, type LeaderboardEntry } from './client';
import { AudioEngine } from './audio';
import { Monetization, type MonetizationOptions } from './monetize';

interface ArcadeContextValue {
  client: ArcadeClient;
  audio: AudioEngine;
  monetization: Monetization;
  user: ArcadeUser | null;
}

const ArcadeContext = createContext<ArcadeContextValue | null>(null);

export interface ArcadeProviderProps {
  gameId: string;
  baseUrl?: string;
  monetization?: MonetizationOptions;
  children: React.ReactNode;
}

export function ArcadeProvider({ gameId, baseUrl, monetization: monOpts, children }: ArcadeProviderProps) {
  const client = useMemo(() => new ArcadeClient({ gameId, baseUrl }), [gameId, baseUrl]);
  const audio = useMemo(() => new AudioEngine(), []);
  const monetization = useMemo(() => new Monetization(monOpts), [monOpts]);
  const [user, setUser] = useState<ArcadeUser | null>(client.currentUser);

  useEffect(() => {
    const off = client.onAuthChange(setUser);
    void client.refresh();
    return off;
  }, [client]);

  const value = useMemo(() => ({ client, audio, monetization, user }), [client, audio, monetization, user]);
  return <ArcadeContext.Provider value={value}>{children}</ArcadeContext.Provider>;
}

function useArcade(): ArcadeContextValue {
  const ctx = useContext(ArcadeContext);
  if (!ctx) throw new Error('useArcade must be used within <ArcadeProvider>');
  return ctx;
}

export function useAuth() {
  const { client, user } = useArcade();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const login = useCallback(
    async (username: string, password: string) => {
      setBusy(true);
      setError(null);
      try {
        return await client.login(username, password);
      } catch (e: any) {
        setError(e?.code || 'login_failed');
        throw e;
      } finally {
        setBusy(false);
      }
    },
    [client],
  );

  const register = useCallback(
    async (username: string, password: string) => {
      setBusy(true);
      setError(null);
      try {
        return await client.register(username, password);
      } catch (e: any) {
        setError(e?.code || 'register_failed');
        throw e;
      } finally {
        setBusy(false);
      }
    },
    [client],
  );

  const logout = useCallback(() => client.logout(), [client]);

  return { user, isSignedIn: !!user, busy, error, login, register, logout };
}

/** Cloud-synced save hook. Returns the current value + a setter that persists. */
export function useCloudSave<T>(defaultValue: T, slot = 'main') {
  const { client, user } = useArcade();
  const [value, setValue] = useState<T>(() => client.readLocal<T>(slot) ?? defaultValue);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    void client.loadSave<T>(slot).then((v) => {
      if (alive && v != null) setValue(v);
      if (alive) setLoaded(true);
    });
    return () => {
      alive = false;
    };
    // reload when auth changes so a fresh sign-in pulls cloud data
  }, [client, slot, user]);

  const update = useCallback(
    (next: T | ((prev: T) => T)) => {
      setValue((prev) => {
        const resolved = typeof next === 'function' ? (next as (p: T) => T)(prev) : next;
        client.save(resolved, slot);
        return resolved;
      });
    },
    [client, slot],
  );

  return { value, setValue: update, loaded };
}

export function useLeaderboard(limit = 20) {
  const { client } = useArcade();
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setEntries(await client.leaderboard(limit));
    } finally {
      setLoading(false);
    }
  }, [client, limit]);

  const submit = useCallback(
    async (score: number, meta?: unknown) => {
      const res = await client.submitScore(score, meta);
      void refresh();
      return res;
    },
    [client, refresh],
  );

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { entries, loading, refresh, submit };
}

export function useAudioEngine() {
  return useArcade().audio;
}

export function useMonetization() {
  return useArcade().monetization;
}

/** Ensures the audio engine unlocks on the first pointer/key gesture. */
export function useAudioUnlock() {
  const audio = useAudioEngine();
  const done = useRef(false);
  useEffect(() => {
    const unlock = () => {
      if (done.current) return;
      done.current = true;
      void audio.unlock();
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
    window.addEventListener('pointerdown', unlock);
    window.addEventListener('keydown', unlock);
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, [audio]);
}
